import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { PortalPermission } from '../../types/permissions';
import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  Icon,
  Image,
  useColorModeValue,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  DrawerCloseButton,
  DrawerBody,
  useBreakpointValue,
  Divider,
  Collapse,
  Button,
} from '@chakra-ui/react';
import {
  MdDashboard,
  MdPeople,
  MdPayment,
  MdHome,
  MdAssessment,
  MdSettings,
  MdReceipt,
  MdBuild,
  MdCheckCircle,
  MdNotifications,
  MdTrendingUp,
  MdDescription,
  MdRequestPage,
  MdHistory,
  MdExpandMore,
  MdExpandLess,
  MdInsertDriveFile,
} from 'react-icons/md';
import { LanguageSwitcher } from '../LanguageSwitcher';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  translationKey: string;
  path: string;
  icon: typeof MdDashboard;
  adminOnly?: boolean;
  requiredPermission?: PortalPermission | PortalPermission[];
  hideForRoles?: string[];
}

interface SubmenuItem extends Omit<NavItem, 'path'> {
  path: string;
}

interface CollapsibleNavItem {
  translationKey: string;
  icon: typeof MdDashboard;
  requiredPermission?: PortalPermission | PortalPermission[];
  hideForRoles?: string[];
  submenu: SubmenuItem[];
}

const navItems: NavItem[] = [
  { translationKey: 'nav.dashboard', path: '/dashboard', icon: MdDashboard, hideForRoles: ['maintenance_coordinator', 'assistant'] },
  { translationKey: 'nav.maintenance', path: '/maintenance', icon: MdBuild, requiredPermission: 'maintenance:view' },
  { translationKey: 'nav.tenants', path: '/tenants', icon: MdPeople, requiredPermission: 'tenant:view', hideForRoles: ['maintenance_coordinator'] },
  { translationKey: 'nav.payments', path: '/payments', icon: MdPayment, requiredPermission: 'payment:view' },
  { translationKey: 'nav.properties', path: '/properties', icon: MdHome, requiredPermission: 'property:view', hideForRoles: ['maintenance_coordinator'] },
  { translationKey: 'nav.expenses', path: '/expenses', icon: MdReceipt, requiredPermission: 'financial:view' },
  { translationKey: 'nav.reports', path: '/reports', icon: MdAssessment, requiredPermission: 'report:view' },
  { translationKey: 'nav.documents', path: '/documents', icon: MdInsertDriveFile, requiredPermission: 'property:view', hideForRoles: ['maintenance_coordinator'] },
  { translationKey: 'nav.settings', path: '/settings', icon: MdSettings },
];

// Advanced Features - collapsible section
const advancedFeaturesMenu: CollapsibleNavItem = {
  translationKey: 'nav.advancedFeatures',
  icon: MdCheckCircle,
  requiredPermission: ['financial:view', 'payment:view', 'tenant:view', 'report:view'],
  hideForRoles: ['assistant', 'maintenance_coordinator'],
  submenu: [
    { translationKey: 'nav.paymentReminders', path: '/payment-reminders', icon: MdNotifications, requiredPermission: 'payment:view', hideForRoles: ['assistant'] },
    { translationKey: 'nav.commission', path: '/commission', icon: MdTrendingUp, requiredPermission: 'financial:view', hideForRoles: ['assistant'] },
    { translationKey: 'nav.taxReporting', path: '/tax-reporting', icon: MdDescription, requiredPermission: 'report:view', hideForRoles: ['assistant'] },
    { translationKey: 'nav.tenantApplications', path: '/tenant-applications', icon: MdRequestPage, requiredPermission: 'tenant:view', hideForRoles: ['assistant'] },
    { translationKey: 'nav.changeHistory', path: '/tenant-history', icon: MdHistory, requiredPermission: 'tenant:view', hideForRoles: ['assistant'] },
  ],
};

function SidebarContent({ isMobile = false, onClose }: { isMobile?: boolean; onClose?: () => void }) {
  const { t } = useTranslation('common');
  const { isAdmin, userProfile, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();
  const bgColor = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'navy.700');
  const activeBg = useColorModeValue('brand.50', 'navy.700');
  const activeColor = useColorModeValue('brand.600', 'brand.200');
  const hoverBg = useColorModeValue('gray.100', 'navy.700');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  // Check if user can access a nav item
  const canAccessItem = (item: NavItem): boolean => {
    // Check if this item should be hidden for their role
    if (item.hideForRoles && userProfile?.role && item.hideForRoles.includes(userProfile.role)) {
      return false;
    }

    // Check adminOnly
    if (item.adminOnly && !isAdmin) {
      return false;
    }

    // Check required permissions
    if (item.requiredPermission) {
      if (Array.isArray(item.requiredPermission)) {
        return hasAnyPermission(item.requiredPermission);
      } else {
        return hasPermission(item.requiredPermission);
      }
    }

    // No restrictions
    return true;
  };

  // Check if collapsible menu is accessible
  const canAccessCollapsible = (item: CollapsibleNavItem): boolean => {
    // Check if this item should be hidden for their role
    if (item.hideForRoles && userProfile?.role && item.hideForRoles.includes(userProfile.role)) {
      return false;
    }

    // Check if at least one submenu item is accessible
    return item.submenu.some(subitem => canAccessItem(subitem as NavItem));
  };

  return (
    <Box
      bg={bgColor}
      borderRight={isMobile ? 'none' : '1px'}
      borderColor={borderColor}
      w="280px"
      h="100vh"
      position="fixed"
      left={0}
      top={0}
    >
      {/* Logo */}
      <Flex h="40" alignItems="center" mx="8" justifyContent="center">
        <Image 
          src="/dossiloc-logo.png" 
          alt="DossiLoc" 
          h="80px"
        />
      </Flex>

      {/* Navigation */}
      <VStack spacing={2} align="stretch" px={4} pb={20} overflowY="auto" maxH="calc(100vh - 140px)">
        {navItems
          .filter((item) => canAccessItem(item))
          .map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <Box
                key={item.path}
                as={RouterLink}
                to={item.path}
                display="flex"
                alignItems="center"
                p={4}
                borderRadius="xl"
                cursor="pointer"
                bg={isActive ? activeBg : 'transparent'}
                color={isActive ? activeColor : 'gray.700'}
                fontWeight={isActive ? '700' : '600'}
                _hover={{
                  bg: isActive ? activeBg : hoverBg,
                  textDecoration: 'none',
                  transform: 'translateX(4px)',
                }}
                transition="all 0.2s"
                onClick={() => isMobile && onClose && onClose()}
              >
                <Icon as={item.icon} fontSize="24px" mr={4} />
                <Text fontSize="md" fontWeight="inherit">{t(item.translationKey)}</Text>
              </Box>
            );
          })}

        {/* Advanced Features Collapsible Section */}
        {canAccessCollapsible(advancedFeaturesMenu) && (
          <Box>
            <Button
              variant="unstyled"
              display="flex"
              alignItems="center"
              w="full"
              p={4}
              borderRadius="xl"
              cursor="pointer"
              bg={isAdvancedOpen ? activeBg : 'transparent'}
              color={isAdvancedOpen ? activeColor : 'gray.700'}
              fontWeight={isAdvancedOpen ? '700' : '600'}
              fontSize="md"
              _hover={{
                bg: isAdvancedOpen ? activeBg : hoverBg,
                transform: 'translateX(4px)',
              }}
              transition="all 0.2s"
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            >
              <Icon as={advancedFeaturesMenu.icon} fontSize="24px" mr={4} />
              <Text flex={1} textAlign="left" fontWeight="inherit">
                {t(advancedFeaturesMenu.translationKey)}
              </Text>
              <Icon
                as={isAdvancedOpen ? MdExpandLess : MdExpandMore}
                fontSize="20px"
                ml={2}
                transition="transform 0.2s"
              />
            </Button>

            <Collapse in={isAdvancedOpen} animateOpacity>
              <VStack spacing={1} align="stretch" pl={8} pt={2}>
                {advancedFeaturesMenu.submenu
                  .filter((item) => canAccessItem(item as NavItem))
                  .map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                      <Box
                        key={item.path}
                        as={RouterLink}
                        to={item.path}
                        display="flex"
                        alignItems="center"
                        p={3}
                        borderRadius="lg"
                        cursor="pointer"
                        bg={isActive ? activeBg : 'transparent'}
                        color={isActive ? activeColor : 'gray.600'}
                        fontWeight={isActive ? '700' : '500'}
                        fontSize="sm"
                        _hover={{
                          bg: isActive ? activeBg : hoverBg,
                          textDecoration: 'none',
                          transform: 'translateX(2px)',
                        }}
                        transition="all 0.2s"
                        onClick={() => isMobile && onClose && onClose()}
                      >
                        <Icon as={item.icon} fontSize="18px" mr={3} />
                        <Text fontWeight="inherit">{t(item.translationKey)}</Text>
                      </Box>
                    );
                  })}
              </VStack>
            </Collapse>
          </Box>
        )}
      </VStack>

      {/* Language Switcher */}
      <Box position="absolute" bottom={4} left={4} right={4}>
        <Divider mb={4} />
        <LanguageSwitcher variant="button" showIcon={false} />
      </Box>
    </Box>
  );
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  if (isMobile) {
    return (
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <SidebarContent isMobile={true} onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return <SidebarContent isMobile={false} />;
}
